import React, { useContext, useEffect, useState } from "react";
import { useWhitelabel } from "../../contexts/WhitelabelContext";
import NotyfContext from "../../contexts/NotyfContext";
import { useDispatch } from "react-redux";
import useWindowSize from "../../hooks/useWindowSize";
import * as Yup from "yup";
import { Button, Form, Modal, Offcanvas } from "react-bootstrap";
import { Formik } from "formik";
import { updateWhitelistIPAddress } from "../../redux/slices/whitelist";
import { useTranslation } from "react-i18next";
import useDeviceType from "../../hooks/useDeviceType";

const EditWhitelistIPModal = ({
  showEditWhitelistIPModal,
  setShowEditWhitelistIPModal,
  ipAddressToEdit,
}) => {
  const { backgroundColor, fontColor } = useWhitelabel();
  const notyf = useContext(NotyfContext);
  const dispatch = useDispatch();
  const [width] = useWindowSize();
  const deviceType = useDeviceType();
  const isDesktop = deviceType === "desktop";
  const [internalShow, setInternalShow] = useState(showEditWhitelistIPModal);
  const [fadeOut, setFadeOut] = useState(false);
  const { t } = useTranslation();
  const ipPattern =
    /^(?:(?:25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(?:\.|$)){4}(?:-\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3})?$|^(?:\d{1,3}\.){3}\d{1,3}\/\d{1,2}$/;

  useEffect(() => {
    if (showEditWhitelistIPModal && !isDesktop) {
      setFadeOut(false);
      setInternalShow(true);
    }
  }, [showEditWhitelistIPModal, isDesktop]);

  const handleHide = () => {
    setFadeOut(true);
    setTimeout(() => {
      setInternalShow(false);
      setShowEditWhitelistIPModal(false);
    }, 300);
  };

  const initialValues = {
    ipAddress: ipAddressToEdit?.ipAddress ?? "",
  };

  const validation = Yup.object().shape({
    ipAddress: Yup.string()
      .matches(ipPattern, "Invalid IP address or range")
      .required("IP address is required"),
  });

  if (isDesktop) {
    return (
      <Modal
        show={showEditWhitelistIPModal}
        onHide={() => setShowEditWhitelistIPModal(false)}
        centered
        backdrop="static"
        scrollable
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Edit IP Address</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Formik
            initialValues={initialValues}
            validationSchema={validation}
            onSubmit={async (values, { setValues }) => {
              await dispatch(
                updateWhitelistIPAddress({
                  id: ipAddressToEdit.id,
                  data: values,
                })
              )
                .then(() => {
                  notyf.open({
                    type: "success",
                    message: "IP Address updated successfully",
                    duration: 5000,
                    ripple: true,
                    dismissible: false,
                    position: {
                      x: "center",
                      y: "top",
                    },
                  });
                })
                .catch((error) => {
                  notyf.open({
                    type: "danger",
                    message: error,
                    duration: 5000,
                    ripple: true,
                    dismissible: false,
                    position: {
                      x: "center",
                      y: "top",
                    },
                  });
                })
                .finally(() => {
                  setValues(initialValues);
                  setShowEditWhitelistIPModal(false);
                });
            }}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              handleSubmit,
              setValues,
            }) => {
              return (
                <Form onSubmit={handleSubmit}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t("whitelist.ip")}</Form.Label>
                    <Form.Control
                      type="text"
                      name="ipAddress"
                      value={values.ipAddress}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      isInvalid={Boolean(touched.ipAddress && errors.ipAddress)}
                      placeholder="e.g. 192.168.0.1 or 192.168.0.0/24"
                    />
                    {!!touched.ipAddress && (
                      <Form.Control.Feedback type="invalid">
                        {errors.ipAddress}
                      </Form.Control.Feedback>
                    )}
                  </Form.Group>
                  <Modal.Footer>
                    <Button
                      variant="primary"
                      style={{
                        "--dynamic-bg-color": backgroundColor,
                        "--dynamic-font-color": fontColor,
                        border: "none",
                      }}
                      onClick={() => {
                        setValues(initialValues);
                        setShowEditWhitelistIPModal(false);
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="primary"
                      type="submit"
                      style={{
                        "--dynamic-bg-color": backgroundColor,
                        "--dynamic-font-color": fontColor,
                        border: "none",
                      }}
                    >
                      Submit
                    </Button>
                  </Modal.Footer>
                </Form>
              );
            }}
          </Formik>
        </Modal.Body>
      </Modal>
    );
  } else {
    return (
      <Offcanvas
        show={internalShow}
        placement="bottom"
        backdrop="static"
        scrollable={true}
        className={`fullscreen-offcanvas ${fadeOut ? "fade-out" : ""}`}
        onHide={handleHide}
      >
        <Offcanvas.Header closeButton className="fullscreen-offcanvas-header">
          <Offcanvas.Title>Edit IP Address</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body className="fullscreen-offcanvas-body">
          <Formik
            initialValues={initialValues}
            validationSchema={validation}
            onSubmit={async (values, { setValues }) => {
              await dispatch(
                updateWhitelistIPAddress({ id: ipAddressToEdit, data: values })
              )
                .then(() => {
                  notyf.open({
                    type: "success",
                    message: "IP Address updated successfully",
                    duration: 5000,
                    ripple: true,
                    dismissible: false,
                    position: {
                      x: "center",
                      y: "top",
                    },
                  });
                })
                .catch((error) => {
                  notyf.open({
                    type: "danger",
                    message: error,
                    duration: 5000,
                    ripple: true,
                    dismissible: false,
                    position: {
                      x: "center",
                      y: "top",
                    },
                  });
                })
                .finally(() => {
                  setValues(initialValues);
                  setShowEditWhitelistIPModal(false);
                });
            }}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              handleSubmit,
              setValues,
            }) => {
              return (
                <Form onSubmit={handleSubmit}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t("whitelist.ip")}</Form.Label>
                    <Form.Control
                      type="text"
                      name="ipAddress"
                      value={values.ipAddress}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      isInvalid={Boolean(touched.ipAddress && errors.ipAddress)}
                      placeholder="e.g. 192.168.0.1 or 192.168.0.0/24"
                    />
                    {!!touched.ipAddress && (
                      <Form.Control.Feedback type="invalid">
                        {errors.ipAddress}
                      </Form.Control.Feedback>
                    )}
                  </Form.Group>
                  <div className="d-flex justify-content-center flex-column mt-3">
                    <Button
                      variant="primary"
                      type="submit"
                      className="mb-3"
                      style={{
                        "--dynamic-bg-color": backgroundColor,
                        "--dynamic-font-color": fontColor,
                        border: "none",
                        padding: "10px",
                      }}
                    >
                      Submit
                    </Button>
                    <Button
                      variant="primary"
                      style={{
                        "--dynamic-bg-color": backgroundColor,
                        "--dynamic-font-color": fontColor,
                        border: "none",
                        padding: "10px",
                      }}
                      onClick={() => {
                        setValues(initialValues);
                        setShowEditWhitelistIPModal(false);
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                </Form>
              );
            }}
          </Formik>
        </Offcanvas.Body>
      </Offcanvas>
    );
  }
};

export default EditWhitelistIPModal;
